import {
  Box,
  Typography,
  Divider,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import useReplacementCharacters from "../hooks/use-replacement-characters";
import useReplacementCharacterManagement from "./use-replacement-character-management";
import ReplacementCharactersTable from "./replacement-characters-table";
import ReplacementCharacterFormDialog from "./replacement-character-form-dialog";
import ReplacementCharacterDeleteDialog from "./replacement-character-delete-dialog";

interface TemplateReplacementCharactersProps {
  templateId: number;
}

const TemplateReplacementCharacters = ({
  templateId,
}: TemplateReplacementCharactersProps) => {
  const { replacementCharacters, isLoading, mutate } =
    useReplacementCharacters(templateId);

  const {
    openDialog,
    value,
    replacement,
    editIndex,
    setValue,
    setReplacement,
    handleOpenDialog,
    handleOpenEditDialog,
    handleCloseDialog,
    handleSave,
    deleteConfirmOpen,
    indexToDelete,
    isDeleting,
    handleRemove,
    handleConfirmDelete,
    handleCancelDelete,
  } = useReplacementCharacterManagement({
    templateId,
    characters: replacementCharacters || [],
    mutate,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 3,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" fontWeight={600}>
              Replacement Characters
            </Typography>
            <Chip
              label={`${replacementCharacters?.length ?? 0} active`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          </Box>
          <IconButton
            size="small"
            color="success"
            onClick={handleOpenDialog}
            aria-label="Add new replacement character"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />

        <Box
          sx={{
            border: 1,
            borderColor: "grey.300",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <ReplacementCharactersTable
            characters={replacementCharacters || []}
            onEdit={handleOpenEditDialog}
            onDelete={handleRemove}
          />
        </Box>
      </Box>

      <ReplacementCharacterFormDialog
        open={openDialog}
        isEditMode={editIndex !== null}
        value={value}
        replacement={replacement}
        onClose={handleCloseDialog}
        onValueChange={setValue}
        onReplacementChange={setReplacement}
        onSave={handleSave}
      />

      <ReplacementCharacterDeleteDialog
        open={deleteConfirmOpen}
        character={
          indexToDelete !== null && replacementCharacters?.[indexToDelete]
            ? replacementCharacters[indexToDelete]
            : null
        }
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

export default TemplateReplacementCharacters;
